<div
  class="hx-conditional-table-grid"
  role="grid"
  [attr.aria-label]="'Conditional Table Grid: ' + questionLabel()"
  [attr.aria-rowcount]="rows().length"
  [attr.aria-colcount]="visibleColumns().length + (showSerialNumber() ? 2 : 1)"
  [style.--hx-conditional-table-grid-question-left.px]="showSerialNumber() ? 50 : 0">
  <hx-table
    maxWidth="100%"
    maxHeight="100%"
    [freezeHeader]="headerFrozen()"
    [freezeHeaderMaxHeight]="freezeHeaderMaxHeight()"
    hxGridTabNav
    hxGridTabNavRowSelector="hx-table-header, hx-table-row"
    [resizableColumns]="true"
    [enableRowHover]="true"
    [id]="tableId()"
    (columnsResized)="onColumnsResized($event)">
    <!-- Header Row -->
    <hx-table-header class="hx-conditional-table-grid__header">
      <ng-template #configureColumnsBtnTpl>
        <button
          type="button"
          #configureColumnsBtn
          class="hx-conditional-table-grid__configure-columns-btn"
          [class.is-configured]="hasCustomColumnConfig()"
          title="Configure columns"
          aria-label="Configure columns"
          (click)="toggleColumnConfig(configureColumnsBtn); $event.stopPropagation()">
          <hx-icon svgIcon="columns" size="14" />
        </button>
      </ng-template>
      @if (showSerialNumber()) {
        <hx-header-cell [width]="effectiveSerialNumberWidth()" [tooltip]="'#'" [align]="'center'">
          <div class="hx-conditional-table-grid__header-cell-content">
            @if (showConfigureColumns() && configureColumnsHost() === 'serial') {
              <ng-container *ngTemplateOutlet="configureColumnsBtnTpl"></ng-container>
            }
            <span>#</span>
          </div>
        </hx-header-cell>
      }
      <hx-header-cell
        align="left"
        width="20%"
        [tooltip]="tooltip() || questionLabel()"
        [tooltipPosition]="tooltipPosition()"
        [tooltipColor]="tooltipColor()">
        <div class="hx-conditional-table-grid__header-cell-content">
          @if (showConfigureColumns() && configureColumnsHost() === 'questionLabel') {
            <ng-container *ngTemplateOutlet="configureColumnsBtnTpl"></ng-container>
          }
          <span>{{ questionLabel() }}</span>
        </div>
      </hx-header-cell>
      @for (col of visibleColumns(); track col.id; let i = $index) {
        <hx-header-cell [width]="getColumnWidth(col)" [align]="'center'" [tooltip]="col.label">
          <div class="hx-conditional-table-grid__header-content">
            <span>{{ col.label }}</span>
            @if (shouldShowSelectAllForColumn(col)) {
              <hx-radio
                class="hx-conditional-table-grid__select-all-label"
                [name]="'select-all-header-' + col.id"
                [attr.aria-label]="'Select all ' + col.label"
                [value]="true"
                [(ngModel)]="selectAllStates()[col.id]"
                [disabled]="isSelectAllDisabledForColumn(col.id) || readonlyMode()"
                (change)="toggleSelectAllForColumn(col.id)">
                All
              </hx-radio>
            }
          </div>
        </hx-header-cell>
      }
      @if (showActions()) {
        <hx-header-cell width="60px" [tooltip]="'Actions'">Actions</hx-header-cell>
      }
    </hx-table-header>

    <!-- Data Rows -->
    @for (row of rows(); track row.linkId; let rowIdx = $index) {
      <hx-table-row
        [class.hx-conditional-table-grid__row--disabled]="readonlyMode()"
        [class.row-error]="shouldShowError(row)">
        @if (showSerialNumber()) {
          <hx-table-cell class="hx-conditional-table-grid__serial">
            {{ rowIdx + 1 }}
          </hx-table-cell>
        }

        <!-- Question Label Cell -->
        <hx-table-cell class="hx-conditional-table-grid__question">
          {{ row.label }}
          @if (requiredMode() === 'all-rows' || requiredMode() === 'custom') {
            <span class="hx-conditional-table-grid__required-star">*</span>
          }
        </hx-table-cell>

        <!-- Dynamic Input Cells -->
        @for (col of visibleColumns(); track col.id) {
          <hx-table-cell
            align="center"
            align="center"
            class="hx-conditional-table-grid__cell"
            [class.hx-conditional-table-grid__cell--disabled]="!isCellEnabled(row.linkId, col.id)">
            @if (getCellMode(row.linkId, col.id) !== 'empty') {
              @switch (col.inputType) {
                @case ('checkbox') {
                  <hx-checkbox
                    [attr.aria-label]="row.label + ' - ' + col.label"
                    [options]="[{ label: '', value: 'checked' }]"
                    [selectedValues]="getCellDisplayValue(row.linkId, col.id) === true ? ['checked'] : []"
                    [disabled]="readonlyMode() || !isCellEnabled(row.linkId, col.id)"
                    [error]="isCellHasError(col, row)"
                    (selectedValuesChange)="onCheckboxValuesChange(row.linkId, col.id, $event)"></hx-checkbox>
                }
                @case ('radio') {
                  <hx-radio-group
                    [attr.aria-label]="row.label + ' - ' + col.label"
                    [value]="getCellDisplayValue(row.linkId, col.id)"
                    [disabled]="readonlyMode() || !isCellEnabled(row.linkId, col.id)"
                    [error]="isCellHasError(col, row)"
                    (change)="onRadioChange(row.linkId, col.id, $event)">
                    @for (option of getRadioOptions(col, row.linkId); track option.id) {
                      <hx-radio [value]="option.value">{{ option.label }}</hx-radio>
                    }
                  </hx-radio-group>
                }
                @case ('dropdown') {
                  <hx-dropdown-with-label
                    [id]="row.linkId + '::' + col.id"
                    [ngModel]="getCellDisplayValue(row.linkId, col.id)"
                    [disabled]="readonlyMode() || !isCellEnabled(row.linkId, col.id)"
                    [placeholder]="'-- Select --'"
                    [required]="isCellRequired(col, row)"
                    [error]="isCellHasError(col, row)"
                    [options]="getDropdownOptions(col, row.linkId)"
                    [multiple]="isDropdownMultiple(col, row.linkId)"
                    [showOtherInput]="shouldShowOtherInput(col, row.linkId)"
                    [otherValue]="getOtherOptionValue(col, row.linkId)"
                    [otherLabel]="
                      getCellControlProp(row.linkId, col.id, 'otherLabel') || col.otherLabel || 'Please specify'
                    "
                    [otherPlaceholder]="
                      getCellControlProp(row.linkId, col.id, 'otherPlaceholder') ||
                      col.otherPlaceholder ||
                      'Enter details...'
                    "
                    [otherInputControl]="getOtherInputControl(row.linkId, col.id)"
                    [showTags]="shouldShowTags(col, row.linkId)"
                    panelClass="hx-dropdown-panel--compact"
                    size="sm"
                    (valueChange)="onDropdownChange(row.linkId, col.id, $event)"></hx-dropdown-with-label>

                  @if (cellSubcontrolTemplate()) {
                    <ng-container
                      [ngTemplateOutlet]="cellSubcontrolTemplate()"
                      [ngTemplateOutletContext]="{
                        rowLinkId: row.linkId,
                        colId: col.id,
                        value: getCellDisplayValue(row.linkId, col.id),
                        row: row,
                        column: col,
                      }"></ng-container>
                  }
                }
                @case ('text') {
                  @if (col.isReadOnly) {
                    <span
                      class="hx-conditional-table-grid__static-text"
                      [attr.aria-label]="row.label + ' - ' + col.label">
                      {{ getReadOnlyTextDisplay(row.linkId, col.id) }}
                    </span>
                  } @else {
                    <hx-input
                      [attr.aria-label]="row.label + ' - ' + col.label"
                      [value]="getCellDisplayValue(row.linkId, col.id)?.toString() ?? ''"
                      [disabled]="readonlyMode() || !isCellEnabled(row.linkId, col.id)"
                      [error]="isCellHasError(col, row)"
                      (valueChange)="onTextChange(row.linkId, col.id, $event.value)"></hx-input>
                  }
                }
                @case ('date') {
                  @if (shouldUseCalculatedDateFallback(row.linkId, col)) {
                    @if (getCalculatedDateFallbackInputType(col) === 'text') {
                      <hx-input
                        [attr.aria-label]="row.label + ' - ' + col.label"
                        [value]="getCalculatedDateFallbackValue(row.linkId, col)"
                        [disabled]="readonlyMode() || !isCellEnabled(row.linkId, col.id)"
                        (valueChange)="onTextChange(row.linkId, col.id, $event.value)"></hx-input>
                    } @else {
                      <hx-datepicker
                        [attr.aria-label]="row.label + ' - ' + col.label"
                        [formControl]="getDateCellFormControl(row.linkId, col)"
                        [mode]="getDateMode(col)"
                        [displayFormat]="getDateDisplayFormat(col)"
                        [min]="getDateBoundary(col, 'min')"
                        [max]="getDateBoundary(col, 'max')"
                        [restrictToPastOrToday]="col.dateConfig?.restrictToPastOrToday ?? false"
                        [restrictPastDate]="col.dateConfig?.restrictPastDate ?? false"
                        [restrictToTodayOrFuture]="col.dateConfig?.restrictToTodayOrFuture ?? false"
                        [restrictFutureDate]="col.dateConfig?.restrictFutureDate ?? false"
                        [restrictToCurrentYear]="col.dateConfig?.restrictToCurrentYear ?? false"
                        [allowedYears]="col.dateConfig?.allowedYears ?? null"
                        [lookBackYears]="col.dateConfig?.lookBackYears ?? null"
                        [disabled]="
                          readonlyMode() || !isCellEnabled(row.linkId, col.id) || col.dateConfig?.readOnlyDate === true
                        "></hx-datepicker>
                    }
                  } @else {
                    <hx-datepicker
                      [attr.aria-label]="row.label + ' - ' + col.label"
                      [formControl]="getDateCellFormControl(row.linkId, col)"
                      [mode]="getDateMode(col)"
                      [displayFormat]="getDateDisplayFormat(col)"
                      [min]="getDateBoundary(col, 'min')"
                      [max]="getDateBoundary(col, 'max')"
                      [restrictToPastOrToday]="col.dateConfig?.restrictToPastOrToday ?? false"
                      [restrictPastDate]="col.dateConfig?.restrictPastDate ?? false"
                      [restrictToTodayOrFuture]="col.dateConfig?.restrictToTodayOrFuture ?? false"
                      [restrictFutureDate]="col.dateConfig?.restrictFutureDate ?? false"
                      [restrictToCurrentYear]="col.dateConfig?.restrictToCurrentYear ?? false"
                      [allowedYears]="col.dateConfig?.allowedYears ?? null"
                      [lookBackYears]="col.dateConfig?.lookBackYears ?? null"
                      [disabled]="
                        readonlyMode() || !isCellEnabled(row.linkId, col.id) || col.dateConfig?.readOnlyDate === true
                      "></hx-datepicker>
                  }
                }
                @case ('rating') {
                  <hx-rating
                    [attr.aria-label]="row.label + ' - ' + col.label + ' rating'"
                    [type]="'star'"
                    [scaleCount]="col.ratingScale ?? 5"
                    [showSlider]="false"
                    [ngModel]="getRatingValue(row.linkId, col.id)"
                    [disabled]="readonlyMode() || !isCellEnabled(row.linkId, col.id)"
                    [error]="isCellHasError(col, row)"
                    (selectionChange)="onRatingChange(row.linkId, col.id, $event)"></hx-rating>
                }
              }
            }
          </hx-table-cell>
        }

        @if (showActions()) {
          <hx-table-cell class="hx-conditional-table-grid__actions">
            <ng-content select="[slot=row-actions]"></ng-content>
          </hx-table-cell>
        }
      </hx-table-row>
    }
  </hx-table>

  <!-- Validation Error Footer -->
  @if (shouldShowTableError()) {
    <div class="hx-conditional-table-grid__error-footer">
      <span class="hx-conditional-table-grid__error-icon">⚠</span>
      {{ validationState().tableErrorMessage }}
    </div>
  }

  @if (showColumnConfig()) {
    <div
      class="hx-conditional-table-grid__column-config-popover"
      [style.top.px]="columnConfigPosition().top"
      [style.left.px]="columnConfigPosition().left"
      (click)="$event.stopPropagation()">
      <div class="hx-conditional-table-grid__column-config-header">
        <span>Configure Columns</span>
        <button type="button" class="hx-conditional-table-grid__column-config-reset" (click)="resetColumnConfig()">
          Reset
        </button>
      </div>
      <hx-checkbox
        class="hx-conditional-table-grid__column-config-option"
        [options]="[{ label: 'Freeze Header', value: 'freeze-header' }]"
        [selectedValues]="headerFrozen() ? ['freeze-header'] : []"
        (selectedValuesChange)="toggleFreezeHeader()"></hx-checkbox>
      @for (col of sortedColumns(); track col.id) {
        <hx-checkbox
          class="hx-conditional-table-grid__column-config-option"
          [options]="[{ label: col.label, value: col.id }]"
          [selectedValues]="!isColumnHidden(col.id) ? [col.id] : []"
          (selectedValuesChange)="toggleColumnVisibility(col.id)"></hx-checkbox>
      }
    </div>
  }
</div>
